<template>
    <page :busy="loading" :compact-mobile-view="true">
        <span v-if="reservationView">
            <div class="reservation-add-button">
                <b-button
                    v-if="addReservationIsVisible"
                    class="w-sm-100"
                    style="width: 100%"
                    size="lg"
                    variant="primary"
                    @click="goToAddReservation()"
                    :disabled="loading"
                    >Add Reservation</b-button
                >
            </div>
            <grid
                ref="grid"
                class="guest-invoices-grid"
                rowSelection="single"
                :columnDefs="columnDefs"
                :rowData="reservations"
                :offset-top="0"
                :paginationPageSize="3"
                :headerHeight="45"
                :isAutoWidth="true"
                :isAutoHeight="true"
                :autoWidthExclude="['length']"
                :isSearch="false"
                :pagination="true"
                :with-space="false"
                :suppressRowDeselection="true"
                :onCellClicked="onCellClicked"
                @selection-changed="handleRowSelect"
                @first-data-rendered="selectCurrentRow"
            >
            </grid>
            <layout-vertical :key="layoutKey" @loading="handleLoading" @refreshReservations="refresh" />
        </span>
    </page>
</template>

<script>
    import { mapGetters } from 'vuex';
    import { mapActions } from 'vuex';
    import Page from '../../generic/page/page.vue';
    import Grid from '../../generic/grid/grid.vue';
    import LayoutVertical from './layout-vertical/layout-vertical.vue';
    import PdfsCellRenderer from './pdfs-cell-renderer.vue';
    import ActionsCellRenderer from './actions-cell-renderer.vue';
    import ReservationsStateCellRenderer from '../../../components/reservations-list-page/reservations-list/reservations-state-cell-renderer.vue';
    import ReservationIconsCellRenderer from '../../../components/reservations-list-page/reservations-list/reservation-icons-cell-renderer.vue';
    import { escapeHTML } from '../../../utils/strings';
    import { isMobileScreen } from '../../../utils/screens';

    export default {
        name: 'GuestInvoices',
        components: {
            Page,
            Grid,
            LayoutVertical,
            pdfsCellRenderer: PdfsCellRenderer,
            actionsCellRenderer: ActionsCellRenderer,
            reservationsStateCellRenderer: ReservationsStateCellRenderer,
            reservationIconsCellRenderer: ReservationIconsCellRenderer,
        },
        data() {
            return {
                loading: false,
                selectedReservationRow: null,
                cellEditorPopup: true,
                isPopup: true,
                layoutKey: 0,
            };
        },
        inject: ['showErrorToast'],
        mounted() {
            (async () => {
                await this.fetchMetadata();
                if (!this.reservations?.length) await this.fetchReservations();
                if (!this.detailsTabTitle) await this.getDetailsTabTitle();
                if (!this.currentRouteTab) {
                    this.replaceTabRouterQuery('setup');
                    this.setActiveTab('setup');
                } else {
                    this.setActiveTab(this.currentRouteTab);
                }
            })();
        },
        computed: {
            ...mapGetters('guest', ['metadata', 'reservations', 'reservation', 'invoicesTab', 'detailsTabTitle']),
            ...mapGetters('header', ['userUnicodeDateFormat']),
            guestId() {
                return parseInt(this.$route.params.id);
            },
            currentRouteReservation() {
                return parseInt(this.$route.query.reservation);
            },
            currentRouteTab() {
                return this.$route.query.tab;
            },
            reservationView() {
                return this.metadata?.permissions?.reservation_view;
            },
            reservationEdit() {
                return this.metadata?.permissions?.reservation_edit;
            },
            columnDefs() {
                return [
                    {
                        field: '',
                        headerName: '',
                        cellRenderer: 'reservationsStateCellRenderer',
                        sortable: false,
                        minWidth: 35,
                        maxWidth: 35,
                        cellStyle: { paddingRight: 0, paddingLeft: '9px' },
                        resizable: false,
                    },
                    {
                        field: '',
                        headerName: '',
                        cellRenderer: 'reservationIconsCellRenderer',
                        sortable: false,
                        cellStyle: { paddingRight: 0, paddingLeft: 0 },
                        maxWidth: 110,
                    },
                    {
                        field: 'id',
                        headerName: 'Inv #',
                        cellRenderer: ({ data }) => {
                            const reservation = data;
                            if (reservation) {
                                return `<a href="/app/reservation/edit/invoice/${reservation.id}">${reservation.id}</a>`;
                            }
                        },
                        minWidth: 150,
                    },
                    {
                        field: 'properties',
                        headerName: 'Property',
                        cellRenderer: ({ data }) => {
                            return data.properties
                                .map((name) => {
                                    return escapeHTML(name);
                                })
                                .join('<br />');
                        },
                        sortable: false,
                        autoHeight: true,
                        minWidth: 350,
                    },
                    {
                        field: 'created',
                        type: 'dateColumn',
                        headerName: 'Entered',
                        minWidth: 160,
                    },
                    {
                        field: 'from_date',
                        type: 'dateColumn',
                        headerName: 'Start',
                        minWidth: 160,
                    },
                    {
                        field: 'to_date',
                        type: 'dateColumn',
                        headerName: 'End',
                        minWidth: 160,
                    },
                    {
                        field: 'length',
                        headerName: 'Nts',
                        resizable: false,
                        minWidth: 100,
                    },
                    {
                        field: '',
                        headerName: 'PDFs',
                        colId: 'action',
                        cellRendererParams: {
                            reportTemplatesChoicesItems: this.metadata.report_templates_choices,
                        },
                        resizable: false,
                        editable: false,
                        sortable: false,
                        minWidth: 190,
                        maxWidth: 190,
                        cellStyle: { paddingRight: 0, paddingLeft: 0 },
                        cellRenderer: 'pdfsCellRenderer',
                    },
                    {
                        field: '',
                        headerName: '',
                        pinned: isMobileScreen() ? undefined : 'right',
                        resizable: false,
                        cellRenderer: 'actionsCellRenderer',
                        sortable: false,
                        minWidth: 230,
                        maxWidth: 230,
                        cellStyle: {
                            paddingRight: '1.5rem',
                            paddingLeft: '.5rem',
                            display: 'flex',
                            justifyContent: 'flex-end',
                        },
                        cellRendererParams: {
                            guestId: this.guestId,
                            refresh: this.refresh,
                            refreshAfterDelete: this.refreshAfterDelete,
                            handleLoading: this.handleLoading,
                        },
                        hide: !this.reservationEdit,
                    },
                ];
            },
        },
        methods: {
            ...mapActions('accept', ['loadAccept']),
            handleLoading(loading) {
                this.loading = loading;
            },
            async refresh(callback) {
                await this.fetchReservations();
                this.selectCurrentRow();
                if (callback) callback();
            },
            async refreshAfterDelete() {
                await this.fetchReservations();
                if (this.reservations?.length) {
                    this.replaceReservationRouterQuery(this.$refs.grid.gridOptions.api.getDisplayedRowAtIndex(0).id);
                }
                this.selectCurrentRow();
            },
            selectCurrentRow() {
                if (this.reservations?.length) {
                    this.$nextTick(() => {
                        if (!this.currentRouteReservation) {
                            this.$refs.grid.gridOptions.api.getDisplayedRowAtIndex(0).setSelected(true);
                            this.replaceReservationRouterQuery(
                                this.$refs.grid.gridOptions.api.getDisplayedRowAtIndex(0).id
                            );
                        } else {
                            this.$refs.grid.gridOptions.api.paginationGoToPage(this.getSelectedReservationPage());
                            this.$refs.grid.gridOptions.api.getRowNode(this.currentRouteReservation).setSelected(true);
                            this.replaceReservationRouterQuery(
                                this.$refs.grid.gridOptions.api.getRowNode(this.currentRouteReservation).id
                            );
                        }
                    });
                }
            },
            replaceReservationRouterQuery(reservationId) {
                this.$router
                    .replace({
                        path: this.$route.path,
                        query: { ...this.$route.query, reservation: reservationId },
                    })
                    .catch((err) => {});
            },
            replaceTabRouterQuery(tab) {
                this.$router
                    .replace({
                        path: this.$route.path,
                        query: { ...this.$route.query, tab: tab },
                    })
                    .catch((err) => {});
            },
            getSelectedReservationPage() {
                return Math.floor(
                    this.$refs.grid.gridOptions.api.getRowNode(this.currentRouteReservation).rowIndex / 3
                );
            },
            handleRowSelect() {
                const gridApi = this.$refs.grid.gridOptions.api;
                const selectedRows = gridApi.getSelectedRows();

                this.selectedReservationRow = selectedRows.length ? selectedRows[0] : null;
                if (!this.selectedReservationRow) {
                    gridApi.getRowNode(this.currentRouteReservation).setSelected(true);
                    this.$nextTick(() => {
                        this.selectedReservationRow = gridApi.getSelectedRows()[0];
                    });
                }
                if (!this.selectedReservationRow) return;
                if (this.selectedReservationRow) this.fetchReservation();
                this.replaceReservationRouterQuery(
                    this.$refs.grid.gridOptions.api.getRowNode(this.selectedReservationRow.id).id
                );
            },
            setActiveTab(tab) {
                this.$store.dispatch('guest/setInvoicesTab', tab);
            },
            scrollToActiveTab(tab) {
                const activeTab = document.getElementById(tab);
                this.$nextTick(() => {
                    setTimeout(() => {
                        activeTab.scrollIntoView({
                            block: 'nearest',
                            inline: 'center',
                        });
                    }, 100);
                });
            },
            async fetchMetadata() {
                try {
                    await this.$store.dispatch('guest/fetchMetadata');
                    if (this.metadata.use_accept_js) {
                        this.loadAccept(this.metadata.accept_js_src_script);
                    }
                } catch (err) {
                    this.showErrorToast({ text: 'Could not load metadata.' });
                }
            },
            async fetchReservations() {
                try {
                    this.loading = true;
                    await this.$store.dispatch('guest/fetchReservations', {
                        guestId: this.guestId,
                    });
                } catch (e) {
                    this.showErrorToast({ text: 'Could not fetch reservations data.' });
                } finally {
                    this.loading = false;
                }
            },
            async fetchReservation() {
                try {
                    this.loading = true;
                    await this.$store.dispatch('guest/fetchReservation', {
                        guestId: this.guestId,
                        reservationId: this.selectedReservationRow?.id,
                    });
                    this.layoutKey++;
                } catch (err) {
                    this.showErrorToast({ text: 'Could not load reservation data.' });
                } finally {
                    this.loading = false;
                    this.scrollToActiveTab(this.currentRouteTab);
                }
            },
            async getDetailsTabTitle() {
                try {
                    this.loading = true;
                    // setTimeout(async () => {
                    await this.$store.dispatch('guest/getDetailsTabTitle', {
                        guestId: this.guestId,
                    });
                    // }, 5000);
                } catch (err) {
                    this.showErrorToast({ text: 'Could not load details tab title.' });
                } finally {
                    this.loading = false;
                }
            },
            onCellClicked(params) {
                if (params.event.target.dataset.action == 'toggle' && params.column.getColId() == 'action') {
                    const cellRendererInstances = params.api.getCellRendererInstances({
                        rowNodes: [params.node],
                        columns: [params.column],
                    });
                    if (cellRendererInstances.length > 0) {
                        const instance = cellRendererInstances[0];
                        instance.togglePopup();
                    }
                }
            },
            goToAddReservation() {
                window.open(`/app/reservation/create?user=${this.guestId}`, '_self');
            },
            addReservationIsVisible() {
                return this.$route.path !== `/app/guest/list/`;
            },
        },
    };
</script>

<style lang="scss" scoped>
    @import './guest-invoices-grid.scss';

    div.reservation-add-button {
        width: 100%;
        display: flex;
        justify-content: center;
        padding: 10px;
    }
    @media screen and (min-width: 992px) {
        div.reservation-add-button {
            visibility: hidden;
            clear: both;
            float: left;
            margin: 10px auto 5px 20px;
            width: 28%;
            display: none;
        }
    }
</style>
